import { CSSProperties, ForwardedRef, forwardRef, useCallback, useEffect, useMemo, useState } from "react";

import { Button, LoadingGroup } from "capitalrx-components-2.0/lib/general";
import classNames from "classnames";
import IMask, { FactoryArg, InputMask } from "imask";

import { useId } from "../../hooks/useId";
import { createComponentStyleHook } from "../../tokens";
import { getBasePropsFromHtmlProps } from "../../utilities/getBasePropsFromHtmlProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { useInputDisabled, useInputReadonly } from "../InputGroup";
import { useSubmitting } from "../SubmitGroup";
import { Overlay } from "../Textbox/Overlay";
import { Placeholder } from "../Textbox/Placeholder";
import { StyledNativeInput } from "../Textbox/StyledNativeInput";
import { useValidity } from "../ValidityGroup";
import { InputContainer } from "../shared";
import { FieldProps } from "./Field";
import { SsnTextboxProps } from "./SsnTextbox";

import tokenStyles from "../Textbox/styles.module.css";
import legacyStyles from "../Textbox/styles.module.scss";

const useResolvedSsnTextboxStyles = createComponentStyleHook<InputProps>({
  legacyStyles,
  tokenStyles,
  componentTokens: "textbox",
});

/**
 * The `Input` component properties.
 */
export interface InputProps extends SsnTextboxProps, Pick<FieldProps, "required"> { }
/**
 * An input field component which allows entry of a SSN value.
 */
export const Input = forwardRef(function Input(props: InputProps, ref: ForwardedRef<HTMLInputElement>) {
  const {
    label,
    masked,
    placeholderChar = "_",
    required = false,
    tooltip,
    instructions,
    hideLabel,
    defaultValue = null,
    autoFocus,
    tabIndex,
    onChange,
    onFocusEnter,
    onFocusExit,
    showToggle,
  } = props;

  const { mode, styles, StyleModeBoundary } = useResolvedSsnTextboxStyles(props);

  const { value = defaultValue } = props;

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [styles.Textbox],
    testIdPrefix: "SsnTextbox",
  });

  const [mask, setMask] = useState<InputMask<FactoryArg> | null>(null);
  const id = useId();
  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);
  const submitting = useSubmitting();
  const [invalid] = useValidity();
  const [isToggled, setIsToggled] = useState(true);
  const [suffixSize, setSuffixSize] = useState<number | null>(null);

  const effectiveMasked = disabled || (showToggle ? isToggled : (masked ?? false));

  // Force concealed when transitioning to disabled state
  useEffect(() => {
    if (disabled && !isToggled) {
      setIsToggled(true);
    }
  }, [disabled]);

  const [currentElement, setCurrentElement] = useState<HTMLInputElement | null>(null);

  const refChangeHandler = useCallback((ref: HTMLInputElement | null) => {
    // Ignore interim null calls caused by ref callback identity changes
    if (!ref) return;
    setCurrentElement(ref);

    setMask((current) => {
      if (ref && !current) {
        // "IMask" is a factory function that creates instances, not a constructor
        // eslint-disable-next-line new-cap
        return IMask(ref, {
          mask: "AAA-BB-CCCC",
          lazy: false,
          placeholderChar,
          blocks: {
            AAA: {
              mask: "000",
              displayChar: effectiveMasked ? "*" : undefined,
            },

            BB: {
              mask: "00",
              displayChar: effectiveMasked ? "*" : undefined,
            },

            CCCC: {
              mask: "0000",
            },
          },
        });
      }

      return current;
    });
  }, []);

  useEffect(() => {
    if (mask) mask.rawInputValue = value?.toString() ?? "";
  }, [value, mask]);

  useEffect(() => {
    if (ref) {
      if (typeof ref === "function") {
        ref(currentElement);
      } else {
        ref.current = currentElement;
      }
    }

    return () => {
      if (ref) {
        if (typeof ref === "function") {
          ref(null);
        } else {
          ref.current = null;
        }
      }
    };
  }, [ref, currentElement]);

  const changeHandler = useCallback(() => {
    if (disabled || readOnly || submitting || !mask) return;

    onChange?.(mask.unmaskedValue, value);
  }, [mask, value, disabled, readOnly, submitting, onChange]);

  useEffect(() => {
    mask?.on("accept", changeHandler);

    return () => {
      mask?.off("accept", changeHandler);
    };
  }, [mask, changeHandler]);

  // Update mask display when effectiveMasked changes (from prop or toggle)
  useEffect(() => {
    if (!mask) return;
    const prevUnmasked = mask.unmaskedValue;
    mask.updateOptions({
      mask: "AAA-BB-CCCC",
      lazy: false,
      placeholderChar,
      blocks: {
        AAA: { mask: "000", displayChar: effectiveMasked ? "*" : undefined },
        BB: { mask: "00", displayChar: effectiveMasked ? "*" : undefined },
        CCCC: { mask: "0000" },
      },
    });
    if (mask.unmaskedValue !== prevUnmasked) {
      mask.unmaskedValue = prevUnmasked;
    }
  }, [mask, effectiveMasked, placeholderChar]);

  const focusHandler = useCallback(() => {
    onFocusEnter?.();
  }, [onFocusEnter]);

  const blurHandler = useCallback(() => {
    onFocusExit?.();
  }, [onFocusExit]);

  const handleMaskedToggle = useCallback(() => {
    setIsToggled((prev) => !prev);
  }, []);

  const overlayLayoutHandler = useCallback((_prefixSize: number | null, suffixSize: number | null) => {
    setSuffixSize(suffixSize);
  }, []);

  const style = useMemo(() => {
    const result: CSSProperties = {};
    if (suffixSize !== null) result.paddingRight = `${suffixSize}px`;
    return result;
  }, [suffixSize]);

  const effectiveSuffix = useMemo(() => {
    if (!showToggle || disabled) return undefined;

    return (
      <Button
        onClick={handleMaskedToggle}
        icon={effectiveMasked ? "eye" : "eye-slash"}
        appearance="text"
        disabled={submitting}
        aria-label={effectiveMasked ? "Show SSN" : "Hide SSN"}
      />
    );
  }, [showToggle, disabled, handleMaskedToggle, effectiveMasked, submitting]);

  return (
    <StyleModeBoundary mode={mode}>
      <LoadingGroup.Placeholder fallback={<Placeholder hasInstructions={!!instructions} />}>
        <InputContainer
          {...getBasePropsFromHtmlProps(baseHtmlProps)}
          id={id}
          label={label}
          instructions={instructions}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          tooltip={tooltip}
          hideLabel={hideLabel}
        >
          <div className="input-container">
            <StyledNativeInput
              ref={refChangeHandler}
              id={id}
              aria-labelledby={`${id}-label`}
              aria-invalid={invalid}
              aria-required={disabled || readOnly ? undefined : required}
              className={classNames("input", { invalid })}
              type="text"
              disabled={disabled}
              autoFocus={autoFocus}
              readOnly={readOnly || submitting}
              tabIndex={disabled || readOnly || submitting ? -1 : (tabIndex ?? 0)}
              style={style}
              onFocus={focusHandler}
              onBlur={blurHandler}
            />
            <Overlay suffix={effectiveSuffix} onLayout={overlayLayoutHandler} />
          </div>
        </InputContainer>
      </LoadingGroup.Placeholder>
    </StyleModeBoundary>
  );
});
